/**
 * A decisão de duplicar um documento — pura, sem `import`, executável fora do build.
 *
 * Card 1864882479341504052. O `DocsService.duplicate` do upstream esperava
 * `waitForSyncReady()`, e esse nome engana: ele resolve quando o ARMAZENAMENTO LOCAL foi
 * consultado — inclusive quando a resposta foi vazia. Num aparelho que ainda não baixou o
 * documento (um iPad abrindo o Space pela primeira vez), a cópia era tirada de um `root` sem
 * filhos, nascia em branco, e o erro morria num `logger.error`. O upstream conhece o sintoma
 * (toeverything/AFFiNE #9620: "All docs → refresh → duplicate → doc vazio") e não o corrigiu.
 *
 * Este módulo responde UMA pergunta: dado o que se sabe sobre a fonte NESTE aparelho, pode
 * copiar? A resposta distingue três coisas que o código antigo confundia:
 *
 *   - "vazio porque ainda não chegou"      → RECUSAR, e dizer por quê;
 *   - "vazio porque o documento é vazio"   → COPIAR (uma cópia fiel de um doc vazio é vazia);
 *   - "tem conteúdo"                       → COPIAR.
 *
 * O que ela NÃO faz, de propósito: não olha bytes. `encodeStateAsUpdate` de um doc que já
 * teve qualquer remoção nunca volta a ser vazio (Regra 8.64 do CLAUDE.md; yjs/yjs#374). A
 * unidade de decisão é o BLOCO — quantos filhos o `root` tem.
 *
 * Os campos vêm do `docState$` do `@affine/nbstore` (`frontend/doc.ts`):
 *   - `loaded`  = o storage local foi consultado (connectedDocs)
 *   - `ready`   = algum update NÃO-vazio foi aplicado a este doc (readyDocs)
 *   - `synced`  = o peer de sync não tem job pendente nem erro para este doc
 */

export interface EstadoDaFonte {
  /** `store.root` existe — todo documento carregado tem um `affine:page`. */
  rootExiste: boolean;
  /** `root.children.length` — nota, superfície do quadro, tudo é filho do root. */
  filhos: number;
  loaded: boolean;
  ready: boolean;
  synced: boolean;
}

export type MotivoDaRecusa = 'nao-carregou' | 'sem-root' | 'ainda-sincronizando';

export type Decisao =
  | { acao: 'copiar' }
  | { acao: 'recusar'; motivo: MotivoDaRecusa };

/**
 * Quanto tempo o `duplicate` espera o peer terminar de trazer a fonte antes de decidir com o
 * que tem. Um canal travado (o caso medido no iPad em 15/09) nunca chega a `synced`; sem teto
 * o botão de duplicar penduraria para sempre. Com o teto, a decisão volta a esta função — e
 * ela recusa, com motivo, em vez de copiar o nada.
 */
export const TETO_DE_ESPERA_MS = 15_000;

export function decidirDuplicacao(estado: EstadoDaFonte): Decisao {
  if (!estado.loaded) {
    return { acao: 'recusar', motivo: 'nao-carregou' };
  }
  if (!estado.rootExiste) {
    return { acao: 'recusar', motivo: 'sem-root' };
  }
  if (estado.filhos > 0) {
    return { acao: 'copiar' };
  }
  // root sem filhos: é vazio de verdade, ou ainda não chegou?
  if (estado.ready) {
    // Houve update não-vazio aplicado: o que está aqui É o documento. Corpo vazio é corpo vazio.
    return { acao: 'copiar' };
  }
  if (estado.synced) {
    // O servidor não tem mais nada para este doc: nasceu vazio e continua vazio.
    return { acao: 'copiar' };
  }
  return { acao: 'recusar', motivo: 'ainda-sincronizando' };
}

/**
 * A precisa esperar pelo peer? Só quando não há conteúdo aplicado E o peer ainda deve algo.
 * Esperar quando `ready` já é `true` custaria o teto inteiro em todo doc grande sem ganho.
 */
export function precisaEsperarOPeer(estado: Pick<EstadoDaFonte, 'ready' | 'synced'>): boolean {
  return !estado.ready && !estado.synced;
}

/** O erro que atravessa o serviço até o hook da interface. Nome estável: o hook faz `instanceof`. */
export class DuplicacaoRecusada extends Error {
  readonly motivo: MotivoDaRecusa;
  readonly sourceDocId: string;
  constructor(motivo: MotivoDaRecusa, sourceDocId: string) {
    super(`duplicação recusada (${motivo}): a fonte ${sourceDocId} ainda não está inteira neste aparelho`);
    this.name = 'DuplicacaoRecusada';
    this.motivo = motivo;
    this.sourceDocId = sourceDocId;
  }
}
